/**
 * src/shell/modules/ModuleSandboxBridge.tsx
 *
 * Micro-Frontend Sandbox Bridge & Interactive Slot Renderer (Sprint 3 / Issue 3.3, Sprint 4).
 * Safely mounts and renders sandboxed widgets for declared slots (header, sidebar, floating).
 */

import React, { useState, useEffect } from 'react';
import { ModuleSlotEntry, sovereignModuleSlotManager } from './SovereignModuleSlotManager.js';
import { sovereignEventBus } from './services/SovereignEventBus.js';
import { sovereignModuleStorage } from './services/SovereignModuleStorage.js';
import { Clock, Calculator, Key, ShieldCheck, X, CheckCircle2, Lock, Unlock, Play, RefreshCw, Cpu, Radio, Database, Film, Video, Settings2, TrendingUp, Activity, DollarSign } from 'lucide-react';
import { FloatingTraderModal as TraderModalImpl } from '../../../modules-optional/trader/src/FloatingTraderModal.js';

interface ModuleSandboxBridgeProps {
  entry: ModuleSlotEntry;
  slot: 'header' | 'sidebar' | 'footer' | 'floating' | 'room' | 'main:pane' | 'top:split';
}

export const ModuleSandboxBridge: React.FC<ModuleSandboxBridgeProps> = ({ entry, slot }) => {
  // ───────────────────────────────────────────────────────────────────────────
  // 1. Header Slot Renderers
  // ───────────────────────────────────────────────────────────────────────────
  if (slot === 'header') {
    if (entry.moduleId === 'mod-clock') {
      return <HeaderClockWidget entry={entry} />;
    }
    return (
      <button
        onClick={() => sovereignModuleSlotManager.openModuleModal(entry.moduleId)}
        className="sovereign-module-header-btn"
        title={`${entry.moduleName} v${entry.version}`}
      >
        <Clock className="w-3.5 h-3.5" style={{ color: 'var(--theme-accent, #38bdf8)' }} />
        <span>{entry.label || entry.moduleName}</span>
      </button>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 2. Sidebar Slot Renderers
  // ───────────────────────────────────────────────────────────────────────────
  if (slot === 'sidebar') {
    let IconComp = Cpu;
    if (entry.moduleId === 'mod-calculator') IconComp = Calculator;
    if (entry.moduleId === 'mod-door-release') IconComp = Key;
    if (entry.moduleId === 'mod-clock') IconComp = Clock;
    if (entry.moduleId === 'mod-video-embed') IconComp = Film;
    if (entry.moduleId === 'mod-trader') IconComp = TrendingUp;

    return (
      <button
        onClick={() => sovereignModuleSlotManager.openModuleModal(entry.moduleId)}
        className="sovereign-module-sidebar-btn group"
        title={`Open ${entry.moduleName} (v${entry.version})`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconComp
            className="w-4 h-4 transition-transform group-hover:scale-110"
            style={{ color: 'var(--theme-accent, #38bdf8)', flexShrink: 0 }}
          />
          <span style={{ color: 'var(--theme-text-primary, #f8fafc)', fontWeight: 600 }}>
            {entry.label || entry.moduleName}
          </span>
        </div>
        <span className="sovereign-module-badge">
          v{entry.version}
        </span>
      </button>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 3. Floating Slot Renderers (Modal Drawers)
  // ───────────────────────────────────────────────────────────────────────────
  if (slot === 'floating') {
    if (entry.moduleId === 'mod-clock') {
      return <FloatingClockModal entry={entry} />;
    }
    if (entry.moduleId === 'mod-calculator') {
      return <FloatingCalculatorModal entry={entry} />;
    }
    if (entry.moduleId === 'mod-door-release') {
      return <FloatingDoorReleaseModal entry={entry} />;
    }
    if (entry.moduleId === 'mod-video-embed') {
      return <FloatingVideoEmbedModal entry={entry} />;
    }
    if (entry.moduleId === 'mod-trader') {
      return <FloatingTraderModal entry={entry} />;
    }
    return (
      <GenericFloatingModal entry={entry} />
    );
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-clock Header Pill
// ─────────────────────────────────────────────────────────────────────────────
function HeaderClockWidget({ entry }: { entry: ModuleSlotEntry }) {
  const [timeStr, setTimeStr] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTimeStr(now.toUTCString().slice(17, 25) + ' UTC');
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <button
      onClick={() => sovereignModuleSlotManager.openModuleModal('mod-clock')}
      className="sovereign-module-header-btn group"
      title={`Consensus Time Sync (mod-clock v${entry.version}) - Click for details`}
    >
      <span
        style={{ backgroundColor: 'var(--theme-accent, #38bdf8)' }}
        className="w-2 h-2 rounded-full animate-ping group-hover:animate-none flex-shrink-0"
      />
      <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-accent, #38bdf8)' }} />
      <span>{timeStr || '--:--:-- UTC'}</span>
      <span
        style={{
          fontSize: '9px',
          padding: '1px 4px',
          borderRadius: '3px',
          background: 'var(--theme-status-success-bg, rgba(16,185,129,0.2))',
          color: 'var(--theme-status-success-text, #34d399)',
        }}
      >
        SYNC
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-clock Floating Modal
// ─────────────────────────────────────────────────────────────────────────────
function FloatingClockModal({ entry }: { entry: ModuleSlotEntry }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 100);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #151d2a))',
        borderColor: 'var(--theme-border-highlight, #10b981)',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-md backdrop-blur-xl"
    >
      <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-2.5">
          <Clock className="w-5 h-5 text-[var(--theme-emerald,#10b981)]" />
          <div>
            <h3 style={{ color: 'var(--theme-text-primary, #ffffff)' }} className="text-sm font-semibold">{entry.moduleName}</h3>
            <span style={{ color: 'var(--theme-emerald, #10b981)' }} className="text-[10px] font-mono">v{entry.version} • System Core</span>
          </div>
        </div>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{ color: 'var(--theme-text-muted, #94a3b8)' }}
          className="p-1.5 rounded-lg hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="my-6 text-center space-y-2">
        <div style={{ color: 'var(--theme-emerald, #10b981)' }} className="text-3xl font-mono font-bold tracking-wider">
          {now.toUTCString().slice(17, 25)}
          <span style={{ color: 'var(--theme-text-muted, rgba(16,185,129,0.7))' }} className="text-sm font-normal ml-2">UTC</span>
        </div>
        <div style={{ color: 'var(--theme-text-muted, #94a3b8)' }} className="text-xs font-mono">
          Epoch ms: {now.getTime()} | Drift: ±0.02ms
        </div>
      </div>

      <div
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(255,255,255,0.04))',
          borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
        }}
        className="rounded-xl p-3 border space-y-2 text-xs font-mono"
      >
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>Consensus Stratum:</span>
          <span style={{ color: 'var(--theme-emerald, #10b981)' }}>Stratum 1 (Local Peer Mesh)</span>
        </div>
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>Cryptographic Hash:</span>
          <span style={{ color: 'var(--theme-text-muted, #94a3b8)' }} className="truncate max-w-[180px]">Ed25519-Signed Verified</span>
        </div>
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>Capabilities Granted:</span>
          <span style={{ color: 'var(--theme-accent, #38bdf8)' }}>time:sync, ui:widget, theme</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-calculator Floating Modal
// ─────────────────────────────────────────────────────────────────────────────
function FloatingCalculatorModal({ entry }: { entry: ModuleSlotEntry }) {
  const [display, setDisplay] = useState('0');
  const [prevVal, setPrevVal] = useState<number | null>(null);
  const [op, setOp] = useState<string | null>(null);
  const [tape, setTape] = useState<string[]>([]);

  useEffect(() => {
    // Load persisted tape from scoped module storage
    sovereignModuleStorage.get<string[]>('mod-calculator', 'tape').then((saved) => {
      if (Array.isArray(saved) && saved.length > 0) {
        setTape(saved);
      }
    });
  }, []);

  const handleNum = (n: string) => {
    setDisplay((prev) => (prev === '0' ? n : prev + n));
  };

  const handleOp = (nextOp: string) => {
    setPrevVal(parseFloat(display));
    setOp(nextOp);
    setDisplay('0');
  };

  const handleEq = () => {
    if (prevVal === null || op === null) return;
    const current = parseFloat(display);
    let res = 0;
    if (op === '+') res = prevVal + current;
    if (op === '-') res = prevVal - current;
    if (op === '×') res = prevVal * current;
    if (op === '÷') res = current !== 0 ? prevVal / current : 0;
    
    const resStr = String(res);
    setDisplay(resStr);

    const expr = `${prevVal} ${op} ${current} = ${resStr}`;
    const newTape = [expr, ...tape].slice(0, 5);
    setTape(newTape);

    // 1. Scoped Storage persistence
    sovereignModuleStorage.set('mod-calculator', 'tape', newTape);

    // 2. Inter-Module Event Bus publish
    sovereignEventBus.publish('mod-calculator', 'escrow:calculation:completed', {
      expression: expr,
      result: res,
      timestamp: Date.now(),
    });

    setPrevVal(null);
    setOp(null);
  };

  const handleClear = () => {
    setDisplay('0');
    setPrevVal(null);
    setOp(null);
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #151d2a))',
        borderColor: 'var(--theme-border-highlight, #38bdf8)',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-sm backdrop-blur-xl"
    >
      <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-2.5">
          <Calculator className="w-5 h-5 text-[var(--theme-accent,#38bdf8)]" />
          <div>
            <h3 style={{ color: 'var(--theme-text-primary, #ffffff)' }} className="text-sm font-semibold">{entry.moduleName}</h3>
            <span style={{ color: 'var(--theme-accent, #38bdf8)' }} className="text-[10px] font-mono">v{entry.version} • System Core</span>
          </div>
        </div>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{ color: 'var(--theme-text-muted, #94a3b8)' }}
          className="p-1.5 rounded-lg hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(0,0,0,0.4))',
          borderColor: 'var(--theme-border, rgba(255,255,255,0.1))',
        }}
        className="my-4 border rounded-xl p-3 text-right"
      >
        <div style={{ color: 'var(--theme-text-muted, #94a3b8)' }} className="text-[10px] font-mono h-4">
          {prevVal !== null && op ? `${prevVal} ${op}` : ''}
        </div>
        <div style={{ color: 'var(--theme-accent, #38bdf8)' }} className="text-2xl font-mono font-bold truncate">{display}</div>
      </div>

      <div className="grid grid-cols-4 gap-2 font-mono text-sm">
        {['C', '±', '%', '÷'].map((k) => (
          <button
            key={k}
            onClick={() => (k === 'C' ? handleClear() : handleOp(k))}
            style={{
              backgroundColor: 'var(--theme-bg-card-hover, rgba(255,255,255,0.05))',
              borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
              color: 'var(--theme-accent, #38bdf8)',
            }}
            className="p-2.5 rounded-lg border font-semibold hover:opacity-80 transition-opacity"
          >
            {k}
          </button>
        ))}
        {['7', '8', '9', '×'].map((k) => (
          <button
            key={k}
            onClick={() => ('0123456789'.includes(k) ? handleNum(k) : handleOp(k))}
            style={{
              backgroundColor: 'var(--theme-bg-card, rgba(255,255,255,0.05))',
              borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
              color: 'var(--theme-text-primary, #ffffff)',
            }}
            className="p-2.5 rounded-lg border hover:opacity-80 transition-opacity"
          >
            {k}
          </button>
        ))}
        {['4', '5', '6', '-'].map((k) => (
          <button
            key={k}
            onClick={() => ('0123456789'.includes(k) ? handleNum(k) : handleOp(k))}
            style={{
              backgroundColor: 'var(--theme-bg-card, rgba(255,255,255,0.05))',
              borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
              color: 'var(--theme-text-primary, #ffffff)',
            }}
            className="p-2.5 rounded-lg border hover:opacity-80 transition-opacity"
          >
            {k}
          </button>
        ))}
        {['1', '2', '3', '+'].map((k) => (
          <button
            key={k}
            onClick={() => ('0123456789'.includes(k) ? handleNum(k) : handleOp(k))}
            style={{
              backgroundColor: 'var(--theme-bg-card, rgba(255,255,255,0.05))',
              borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
              color: 'var(--theme-text-primary, #ffffff)',
            }}
            className="p-2.5 rounded-lg border hover:opacity-80 transition-opacity"
          >
            {k}
          </button>
        ))}
        <button
          onClick={() => handleNum('0')}
          style={{
            backgroundColor: 'var(--theme-bg-card, rgba(255,255,255,0.05))',
            borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
            color: 'var(--theme-text-primary, #ffffff)',
          }}
          className="col-span-2 p-2.5 rounded-lg border hover:opacity-80 transition-opacity"
        >
          0
        </button>
        <button
          onClick={() => handleNum('.')}
          style={{
            backgroundColor: 'var(--theme-bg-card, rgba(255,255,255,0.05))',
            borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
            color: 'var(--theme-text-primary, #ffffff)',
          }}
          className="p-2.5 rounded-lg border hover:opacity-80 transition-opacity"
        >
          .
        </button>
        <button
          onClick={handleEq}
          style={{
            backgroundColor: 'var(--theme-accent, #0284c7)',
            color: 'var(--theme-text-inverse, #ffffff)',
          }}
          className="p-2.5 rounded-lg font-bold hover:opacity-90 transition-opacity"
        >
          =
        </button>
      </div>

      {tape.length > 0 && (
        <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="mt-4 pt-3 border-t">
          <div className="flex items-center justify-between text-[10px] font-mono mb-1.5" style={{ color: 'var(--theme-text-muted, #94a3b8)' }}>
            <span className="flex items-center gap-1"><Database className="w-3 h-3 text-[var(--theme-accent,#38bdf8)]" /> Scoped Tape:</span>
            <span style={{ color: 'var(--theme-accent, #38bdf8)' }}>storage:local</span>
          </div>
          <div
            style={{
              backgroundColor: 'var(--theme-bg-input, rgba(0,0,0,0.3))',
              color: 'var(--theme-text-secondary, #cbd5e1)',
            }}
            className="rounded-lg p-2 font-mono text-[11px] space-y-0.5 max-h-20 overflow-y-auto"
          >
            {tape.map((line, idx) => (
              <div key={idx} className="truncate hover:opacity-100 opacity-80 transition-opacity">{line}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-door-release Floating Modal
// ─────────────────────────────────────────────────────────────────────────────
function FloatingDoorReleaseModal({ entry }: { entry: ModuleSlotEntry }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [logs, setLogs] = useState<string[]>(['[00:00:00] Door Relay Standby (Locked)']);

  // Load last release timestamp from scoped storage
  useEffect(() => {
    sovereignModuleStorage.get<number>('mod-door-release', 'last_pulse').then((ts) => {
      if (ts) {
        const time = new Date(ts).toLocaleTimeString();
        setLogs((prev) => [`[${time}] Last Recorded Release (Scoped KV)`, ...prev.slice(0, 3)]);
      }
    });
  }, []);

  // Listen to cross-module bus events (e.g. escrow calculations)
  useEffect(() => {
    const sub = sovereignEventBus.subscribe<{ expression?: string; result?: number }>(
      'mod-door-release',
      'escrow:calculation:completed',
      (data) => {
        const expr = data?.expression || String(data?.result ?? '');
        setLogs((prev) => [`[Bus] Escrow Calc Sync: ${expr}`, ...prev.slice(0, 3)]);
      }
    );
    return () => sub.unsubscribe();
  }, []);

  const handleUnlock = () => {
    setIsUnlocked(true);
    setCountdown(5);
    const now = Date.now();
    const nowStr = new Date(now).toTimeString().slice(0, 8);
    setLogs((prev) => [`[${nowStr}] Door Released by Physical Access Relay`, ...prev.slice(0, 3)]);

    // 1. Persist pulse event
    sovereignModuleStorage.set('mod-door-release', 'last_pulse', now);

    // 2. Publish event to mesh bus
    sovereignEventBus.publish('mod-door-release', 'access:door:released', {
      doorId: 'main-entrance-1',
      timestamp: now,
      method: 'MOMENTARY_STRIKE',
    });
  };

  useEffect(() => {
    if (countdown <= 0) {
      if (isUnlocked) {
        setIsUnlocked(false);
        const nowStr = new Date().toTimeString().slice(0, 8);
        setLogs((prev) => [`[${nowStr}] Auto-Relock Engaged`, ...prev.slice(0, 3)]);
      }
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, isUnlocked]);

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #151d2a))',
        borderColor: 'var(--theme-border-highlight, #f59e0b)',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-md backdrop-blur-xl"
    >
      <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-2.5">
          <Key className="w-5 h-5 text-[var(--theme-amber,#f59e0b)]" />
          <div>
            <h3 style={{ color: 'var(--theme-text-primary, #ffffff)' }} className="text-sm font-semibold">{entry.moduleName}</h3>
            <span style={{ color: 'var(--theme-amber, #f59e0b)' }} className="text-[10px] font-mono">v{entry.version} • Optional Module</span>
          </div>
        </div>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{ color: 'var(--theme-text-muted, #94a3b8)' }}
          className="p-1.5 rounded-lg hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="my-6 text-center space-y-3">
        <div
          style={{
            backgroundColor: isUnlocked ? 'var(--theme-status-success-bg, rgba(16,185,129,0.1))' : 'var(--theme-status-warning-bg, rgba(245,158,11,0.1))',
            borderColor: isUnlocked ? 'var(--theme-emerald, #10b981)' : 'var(--theme-amber, #f59e0b)',
            color: isUnlocked ? 'var(--theme-status-success-text, #34d399)' : 'var(--theme-status-warning-text, #fbbf24)',
          }}
          className="inline-flex p-4 rounded-full border"
        >
          {isUnlocked ? <Unlock className="w-10 h-10 animate-pulse" /> : <Lock className="w-10 h-10" />}
        </div>
        <div className="text-lg font-bold font-mono">
          {isUnlocked ? (
            <span style={{ color: 'var(--theme-status-success-text, #34d399)' }}>DOOR UNLOCKED ({countdown}s)</span>
          ) : (
            <span style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>DOOR SECURED</span>
          )}
        </div>
        <button
          disabled={isUnlocked}
          onClick={handleUnlock}
          style={{
            backgroundColor: 'var(--theme-accent, #38bdf8)',
            color: 'var(--theme-text-inverse, #0b0f19)',
          }}
          className="w-full py-3 px-4 rounded-xl disabled:opacity-50 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:opacity-90"
        >
          <Key className="w-4 h-4" />
          {isUnlocked ? `Relocking in ${countdown}s...` : 'Pulse Strike / Release Door'}
        </button>
      </div>

      <div className="flex items-center justify-between px-1 text-[10px] font-mono" style={{ color: 'var(--theme-text-muted, #94a3b8)' }}>
        <span className="flex items-center gap-1"><Radio className="w-3 h-3 text-[var(--theme-amber,#f59e0b)]" /> Mesh Event Stream</span>
        <span style={{ color: 'var(--theme-amber, #f59e0b)' }}>events:emit | events:listen</span>
      </div>

      <div
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(0,0,0,0.3))',
          borderColor: 'var(--theme-border, rgba(255,255,255,0.05))',
          color: 'var(--theme-text-muted, #94a3b8)',
        }}
        className="rounded-xl p-3 border space-y-1 font-mono text-[11px] mt-1"
      >
        {logs.map((log, i) => (
          <div key={i} className="truncate">{log}</div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-video-embed Floating Modal
// ─────────────────────────────────────────────────────────────────────────────
function FloatingVideoEmbedModal({ entry }: { entry: ModuleSlotEntry }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [channelId, setChannelId] = useState('@herx-mesh-live');
  const [quality, setQuality] = useState('1080p');
  const [autoplay, setAutoplay] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState('••••••••38f2');
  const [tenantBranding, setTenantBranding] = useState('HERX Mesh Media');
  const [allowedProviders, setAllowedProviders] = useState<string[]>(['YOUTUBE', 'VIMEO', 'IPFS']);

  useEffect(() => {
    // Attempt to load resolved user config from API
    fetch('/api/v1/me/modules/mod-video-embed/config', { credentials: 'include' })
      .then((res) => res.json())
      .then((payload) => {
        if (payload?.ok && payload?.data?.config) {
          const cfg = payload.data.config;
          if (cfg['youtube.channelId']) setChannelId(cfg['youtube.channelId']);
          if (cfg['defaultQuality']) setQuality(cfg['defaultQuality']);
          if (cfg['autoplay'] !== undefined) setAutoplay(cfg['autoplay']);
          if (cfg['youtube.apiKey']) setApiKeyMasked(cfg['youtube.apiKey']);
          if (cfg['tenantBranding']) setTenantBranding(cfg['tenantBranding']);
          if (cfg['allowedProviders']) setAllowedProviders(cfg['allowedProviders']);
        }
      })
      .catch(() => {});
  }, []);

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
    sovereignEventBus.publish('mod-video-embed', 'media:stream:playback', {
      channel: channelId,
      isPlaying: !isPlaying,
      quality,
      timestamp: Date.now(),
    });
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #151d2a))',
        borderColor: 'var(--theme-border-highlight, #38bdf8)',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-lg backdrop-blur-xl animate-fadeIn"
    >
      <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-2.5">
          <Film className="w-5 h-5 text-[var(--theme-accent,#38bdf8)]" />
          <div>
            <h3 style={{ color: 'var(--theme-text-primary, #ffffff)' }} className="text-sm font-semibold">{entry.moduleName}</h3>
            <span style={{ color: 'var(--theme-accent, #38bdf8)' }} className="text-[10px] font-mono">v{entry.version} • 3-Tier Config Pilot</span>
          </div>
        </div>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{ color: 'var(--theme-text-muted, #94a3b8)' }}
          className="p-1.5 rounded-lg hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Media Canvas Mock */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(2,132,199,0.15), rgba(99,102,241,0.15))',
          borderColor: 'var(--theme-border, #1e293b)',
        }}
        className="my-5 rounded-xl border p-6 text-center relative overflow-hidden flex flex-col items-center justify-center min-h-[160px]"
      >
        <div className="absolute top-2 left-3 text-[10px] font-mono" style={{ color: 'var(--theme-accent, #38bdf8)' }}>
          {tenantBranding} • Stream Hub
        </div>
        <div className="absolute top-2 right-3 text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'var(--theme-bg-input, #070a10)', color: 'var(--theme-text-muted, #94a3b8)' }}>
          {quality}
        </div>

        <div className="p-4 rounded-full mb-2 cursor-pointer transition-transform hover:scale-105" onClick={handleTogglePlay} style={{ background: 'var(--theme-accent, #38bdf8)', color: 'var(--theme-text-inverse, #0b0f19)' }}>
          {isPlaying ? <Video className="w-8 h-8 animate-pulse" /> : <Play className="w-8 h-8 ml-1" />}
        </div>
        <div className="text-sm font-bold" style={{ color: 'var(--theme-text-primary, #ffffff)' }}>
          {isPlaying ? `Streaming from ${channelId}` : 'Stream Ready on Sovereign Mesh'}
        </div>
        <div className="text-xs" style={{ color: 'var(--theme-text-muted, #94a3b8)' }}>
          Encrypted OAuth token securely held in server vault
        </div>
      </div>

      {/* 3-Tier Hierarchy Inspection Card */}
      <div
        style={{
          backgroundColor: 'var(--theme-bg-input, rgba(0,0,0,0.3))',
          borderColor: 'var(--theme-border, rgba(255,255,255,0.08))',
        }}
        className="rounded-xl p-3 border space-y-2 text-xs font-mono mb-4"
      >
        <div className="flex items-center justify-between pb-1 border-b" style={{ borderColor: 'var(--theme-border-subtle, rgba(255,255,255,0.06))' }}>
          <span className="font-bold text-[11px]" style={{ color: 'var(--theme-accent, #38bdf8)' }}>Resolved Config Hierarchy:</span>
          <span className="text-[10px]" style={{ color: 'var(--theme-text-muted, #94a3b8)' }}>User → Tenant → Global</span>
        </div>
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>1. Global Key (Encrypted):</span>
          <span className="font-semibold" style={{ color: 'var(--theme-status-success-text, #34d399)' }}>{apiKeyMasked}</span>
        </div>
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>2. Tenant Providers:</span>
          <span style={{ color: 'var(--theme-text-primary, #ffffff)' }}>{allowedProviders.join(', ')}</span>
        </div>
        <div className="flex justify-between" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
          <span>3. User Channel:</span>
          <span style={{ color: 'var(--theme-accent, #38bdf8)' }}>{channelId}</span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={handleTogglePlay}
          style={{
            backgroundColor: 'var(--theme-accent, #38bdf8)',
            color: 'var(--theme-text-inverse, #0b0f19)',
          }}
          className="flex-1 py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition hover:opacity-90 cursor-pointer"
        >
          {isPlaying ? 'Pause Stream' : '▶ Start Playback'}
        </button>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{
            backgroundColor: 'var(--theme-bg-input, rgba(255,255,255,0.05))',
            borderColor: 'var(--theme-border, rgba(255,255,255,0.1))',
            color: 'var(--theme-text-primary, #ffffff)',
          }}
          className="py-2.5 px-4 rounded-xl border text-xs font-semibold hover:opacity-80 transition cursor-pointer"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: Generic Floating Modal
// ─────────────────────────────────────────────────────────────────────────────
function GenericFloatingModal({ entry }: { entry: ModuleSlotEntry }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-modal, var(--theme-bg-card, #151d2a))',
        borderColor: 'var(--theme-border, rgba(255,255,255,0.15))',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
      className="border rounded-2xl p-6 shadow-2xl w-full max-w-md backdrop-blur-xl"
    >
      <div style={{ borderColor: 'var(--theme-border, rgba(255,255,255,0.1))' }} className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-2.5">
          <Cpu className="w-5 h-5 text-[var(--theme-accent,#38bdf8)]" />
          <div>
            <h3 style={{ color: 'var(--theme-text-primary, #ffffff)' }} className="text-sm font-semibold">{entry.moduleName}</h3>
            <span style={{ color: 'var(--theme-text-muted, #94a3b8)' }} className="text-[10px] font-mono">v{entry.version}</span>
          </div>
        </div>
        <button
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
          style={{ color: 'var(--theme-text-muted, #94a3b8)' }}
          className="p-1.5 rounded-lg hover:opacity-75 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="my-6 text-center text-sm" style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
        Sandboxed module <span className="font-mono text-[var(--theme-accent,#38bdf8)]">{entry.moduleId}</span> active in slot <span className="font-mono text-[var(--theme-text-primary,#ffffff)]">{entry.slot}</span>.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents: mod-trader Floating Trading Desk
// ─────────────────────────────────────────────────────────────────────────────
function FloatingTraderModal({ entry }: { entry: ModuleSlotEntry }) {
  return (
    <TraderModalImpl
      entry={entry}
      onClose={() => sovereignModuleSlotManager.closeModuleModal()}
    />
  );
}

